import React, { useEffect, useState } from "react";
import {
  CalendarClock,
  Clock,
  Repeat,
  Newspaper,
  PenLine,
  Loader2,
  Plus,
  Trash2,
  Play,
  Pause,
  Sparkles,
  Image as ImageIcon,
  Facebook,
  Instagram,
  Music2,
  Linkedin,
  Eye,
  X,
  Settings2,
  Globe,
  Check,
  Pencil,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ImagePicker from "@/components/admin/ImagePicker";

const fmtErr = (e, f) => e?.response?.data?.detail || e?.message || f || "Something went wrong.";

const CHANNELS = [
  { id: "facebook", label: "Facebook", Icon: Facebook },
  { id: "instagram", label: "Instagram", Icon: Instagram },
  { id: "tiktok", label: "TikTok", Icon: Music2 },
  { id: "linkedin", label: "LinkedIn", Icon: Linkedin },
];
const DAYS = [
  { id: "mon", label: "Mon" }, { id: "tue", label: "Tue" }, { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" }, { id: "fri", label: "Fri" }, { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];

const blank = {
  type: "recurring",
  source: "latest_blog",
  message: "",
  ai_help: true,
  image_url: null,
  image_label: "",
  image_mode: "article",
  channels: ["facebook"],
  days: [],
  time: "19:00",
  run_at: "",
};

const EmpireBuilder = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [connectedSet, setConnectedSet] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Content source (per-customer "latest post" feed)
  const [showSettings, setShowSettings] = useState(false);
  const [source, setSource] = useState({ site_url: "", feed_url: "", resolved_feed_url: "", is_admin: false });
  const [savingSource, setSavingSource] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPost, setTestPost] = useState(null);

  const loadSource = async () => {
    try {
      const { data } = await api.get("/automations/settings");
      setSource(data || {});
    } catch { /* non-fatal */ }
  };

  const saveSource = async () => {
    setSavingSource(true);
    try {
      const { data } = await api.put("/automations/settings", { site_url: source.site_url, feed_url: source.feed_url });
      setSource((s) => ({ ...s, resolved_feed_url: data.resolved_feed_url }));
      toast.success(data.resolved_feed_url ? "Saved — we found your post feed." : "Saved.");
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't save your site."));
    } finally {
      setSavingSource(false);
    }
  };

  const testSource = async () => {
    setTesting(true);
    setTestPost(null);
    try {
      const { data } = await api.post("/automations/settings/test", { site_url: source.site_url, feed_url: source.feed_url });
      setTestPost(data.post);
      toast.success("Feed works — here's your latest post.");
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't read that feed."));
    } finally {
      setTesting(false);
    }
  };

  const [gateBlocked, setGateBlocked] = useState(false);

  const load = async () => {
    try {
      const [{ data }, ayr, meta] = await Promise.all([
        api.get("/automations"),
        api.get("/ayrshare/status").catch(() => ({ data: { platforms: [] } })),
        api.get("/connections/meta/status").catch(() => ({ data: {} })),
      ]);
      setItems(data.items || []);
      const ayrPlats = ayr.data.platforms || [];
      // FB/IG now publish via native Meta; TikTok/LinkedIn via Ayrshare.
      const set = new Set();
      if (meta.data.facebook_connected) set.add("facebook");
      if (meta.data.instagram_connected) set.add("instagram");
      ["tiktok", "linkedin"].forEach((c) => ayrPlats.includes(c) && set.add(c));
      setConnectedSet(set);
    } catch (e) {
      if (e?.response?.status === 402) { setGateBlocked(true); }
      else { toast.error(fmtErr(e, "Couldn't load your automations.")); }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadSource(); }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggle = (key, val) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const payload = () => ({
    type: form.type,
    source: form.source,
    message: form.message,
    ai_help: form.ai_help,
    image_url: form.image_url,
    image_mode: form.image_mode,
    channels: form.channels,
    days: form.days,
    time: form.time,
    run_at: form.type === "oneoff" && form.run_at ? new Date(form.run_at).toISOString() : null,
  });

  const doPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/automations/preview", payload());
      setPreview(data);
    } catch (e) {
      toast.error(fmtErr(e, "Preview failed."));
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    if (form.type === "oneoff" && !form.run_at) return toast.error("Pick a date & time.");
    if (form.type === "oneoff" && new Date(form.run_at).getTime() <= Date.now())
      return toast.error("Pick a time in the future.");
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/automations/${editingId}`, payload());
        toast.success("Automation updated.");
      } else {
        await api.post("/automations", payload());
        toast.success("Automation saved — it'll run itself from now on.");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(blank);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't save."));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (a) => {
    let runAtLocal = "";
    if (a.type === "oneoff" && a.run_at) {
      // ISO (UTC) → value for datetime-local input
      const d = new Date(a.run_at);
      const pad = (n) => String(n).padStart(2, "0");
      runAtLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    setForm({
      type: a.type || "recurring",
      source: a.source || "latest_blog",
      message: a.message || "",
      ai_help: a.ai_help !== false,
      image_url: a.image_url || null,
      image_label: a.image_url ? "Chosen image" : "",
      image_mode: a.image_mode || (a.source === "latest_blog" ? "article" : "auto"),
      channels: a.channels || ["facebook"],
      days: a.days || [],
      time: a.time || "19:00",
      run_at: runAtLocal,
    });
    setEditingId(a.automation_id);
    setPreview(null);
    setShowForm(true);
    setShowSettings(false);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleEnabled = async (a) => {
    try {
      await api.patch(`/automations/${a.automation_id}`, { enabled: !a.enabled });
      load();
    } catch (e) { toast.error(fmtErr(e, "Couldn't update.")); }
  };
  const remove = async (a) => {
    try { await api.delete(`/automations/${a.automation_id}`); load(); }
    catch (e) { toast.error(fmtErr(e, "Couldn't delete.")); }
  };

  return (
    <div className="space-y-6" data-testid="empire-builder">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300 flex items-center gap-2">
            <CalendarClock size={12} /> Automations
          </p>
          <h2 className="font-heading text-2xl font-bold text-white mt-1">Set it once. It posts for you.</h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Tell ClydeFlow what to post and when — like &ldquo;every night at 7pm, post the latest Paisley
            blog to Facebook&rdquo; — and it just happens, hands-free.
          </p>
        </div>
        {!showForm && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowSettings((s) => !s)}
              data-testid="empire-settings-btn"
              className="inline-flex items-center gap-2 px-4 py-3 border border-white/15 text-slate-300 hover:border-cyan-400/50 hover:text-white rounded-sm text-xs uppercase tracking-widest"
            >
              <Settings2 size={14} /> Content source
            </button>
            <button
              onClick={() => { setEditingId(null); setShowForm(true); setForm(blank); setPreview(null); }}
              data-testid="empire-new-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-widest hover:bg-[#33F3FF]"
            >
              <Plus size={14} /> New automation
            </button>
          </div>
        )}
      </div>

      {gateBlocked && (
        <div className="p-6 bg-[#0A192F] border border-amber-400/30 rounded-sm text-center" data-testid="empire-gate">
          <h3 className="font-heading text-xl font-bold text-white">Empire Builder is a paid feature</h3>
          <p className="text-sm text-slate-400 mt-2">
            It&apos;s included on any paid plan and during your free trial. Upgrade to set posts on autopilot.
          </p>
          <a href="/#pricing" className="mt-4 inline-block px-5 py-2.5 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-widest hover:bg-[#33F3FF]">
            See plans
          </a>
        </div>
      )}

      {showSettings && !gateBlocked && (
        <div className="p-5 bg-[#0A192F] border border-cyan-400/20 rounded-sm space-y-3" data-testid="empire-settings">
          <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.3em]">
            <Globe size={13} /> Your content source
          </div>
          <p className="text-sm text-slate-400">
            When an automation posts &ldquo;your latest blog post&rdquo;, we grab the newest article from here.
            Paste your website and we&apos;ll find the feed, or paste a direct RSS/Atom feed URL.
            {source.is_admin && " Leave blank to use the Paisley network feed."}
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Website URL</label>
              <input
                value={source.site_url || ""}
                onChange={(e) => setSource((s) => ({ ...s, site_url: e.target.value }))}
                placeholder="https://yourbusiness.co.uk"
                data-testid="empire-source-site"
                className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Feed URL (optional)</label>
              <input
                value={source.feed_url || ""}
                onChange={(e) => setSource((s) => ({ ...s, feed_url: e.target.value }))}
                placeholder="https://yourbusiness.co.uk/feed"
                data-testid="empire-source-feed"
                className="mt-1 w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
              />
            </div>
          </div>
          {source.resolved_feed_url && (
            <p className="text-[11px] font-mono text-emerald-300 flex items-center gap-1">
              <Check size={12} /> Feed detected: {source.resolved_feed_url}
            </p>
          )}
          {testPost && (
            <div className="p-3 bg-[#050B14] border border-cyan-400/20 rounded-sm" data-testid="empire-source-testpost">
              <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-1">Latest post found</p>
              {testPost.image && <img src={testPost.image} alt="" className="w-full max-h-40 object-cover rounded-sm mb-2" />}
              <p className="text-sm text-white font-medium">{testPost.title}</p>
              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{testPost.summary}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            <button onClick={testSource} disabled={testing} data-testid="empire-source-test"
              className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-400/40 text-cyan-300 hover:border-cyan-400 rounded-sm text-xs uppercase tracking-wider">
              {testing ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Test feed
            </button>
            <button onClick={saveSource} disabled={savingSource} data-testid="empire-source-save"
              className="inline-flex items-center gap-2 px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-widest hover:bg-[#33F3FF] disabled:opacity-50">
              {savingSource ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="p-5 bg-[#0A192F] border border-cyan-400/20 rounded-sm space-y-5" data-testid="empire-form">
          {/* When */}
          <div>
            <p className="text-xs font-bold text-white mb-2">1. How often?</p>
            <div className="flex gap-2">
              <button onClick={() => set({ type: "recurring" })} data-testid="empire-type-recurring"
                className={`flex-1 p-3 rounded-sm border text-left ${form.type === "recurring" ? "border-cyan-400/50 bg-cyan-400/10" : "border-white/10"}`}>
                <Repeat size={16} className="text-cyan-300" />
                <p className="text-sm text-white mt-1">Repeat on a schedule</p>
                <p className="text-xs text-slate-400">e.g. every night at 7pm</p>
              </button>
              <button onClick={() => set({ type: "oneoff" })} data-testid="empire-type-oneoff"
                className={`flex-1 p-3 rounded-sm border text-left ${form.type === "oneoff" ? "border-cyan-400/50 bg-cyan-400/10" : "border-white/10"}`}>
                <Clock size={16} className="text-cyan-300" />
                <p className="text-sm text-white mt-1">Once, at a set time</p>
                <p className="text-xs text-slate-400">e.g. this Saturday at 9pm</p>
              </button>
            </div>
          </div>

          {/* What */}
          <div>
            <p className="text-xs font-bold text-white mb-2">2. What should it post?</p>
            <div className="flex gap-2 mb-3">
              <button onClick={() => set({ source: "latest_blog", image_mode: form.image_mode === "auto" ? "article" : form.image_mode })} data-testid="empire-source-blog"
                className={`flex-1 p-3 rounded-sm border text-left ${form.source === "latest_blog" ? "border-cyan-400/50 bg-cyan-400/10" : "border-white/10"}`}>
                <Newspaper size={16} className="text-cyan-300" />
                <p className="text-sm text-white mt-1">My latest blog post</p>
                <p className="text-xs text-slate-400">
                  {source.site_url || source.feed_url
                    ? `From ${source.site_url || source.feed_url}`
                    : source.is_admin
                      ? "Auto-grabs the newest Paisley article + link"
                      : "Set your site in “Content source” first"}
                </p>
              </button>
              <button onClick={() => set({ source: "custom", image_mode: form.image_mode === "article" ? "auto" : form.image_mode })} data-testid="empire-source-custom"
                className={`flex-1 p-3 rounded-sm border text-left ${form.source === "custom" ? "border-cyan-400/50 bg-cyan-400/10" : "border-white/10"}`}>
                <PenLine size={16} className="text-cyan-300" />
                <p className="text-sm text-white mt-1">My own words</p>
                <p className="text-xs text-slate-400">Type it — I can polish it for you</p>
              </button>
            </div>
            {form.source === "custom" && (
              <div className="space-y-2">
                <textarea value={form.message} onChange={(e) => set({ message: e.target.value })} rows={3}
                  placeholder="What do you want to say?" data-testid="empire-message"
                  className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm resize-none" />
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={form.ai_help} onChange={(e) => set({ ai_help: e.target.checked })}
                    data-testid="empire-aihelp" className="accent-cyan-400" />
                  <Sparkles size={12} className="text-cyan-300" /> Help me write it
                </label>
              </div>
            )}
          </div>

          {/* Image */}
          <div>
            <p className="text-xs font-bold text-white mb-2">3. Image</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { id: "auto", label: "Generate a photo", desc: "Realistic AI photo", Icon: Sparkles },
                { id: "own", label: "My own image", desc: "Pick or upload", Icon: ImageIcon },
                { id: "none", label: "No image", desc: "Text only", Icon: X },
                ...(form.source === "latest_blog"
                  ? [{ id: "article", label: "Blog's photo", desc: "Use article image", Icon: Newspaper }]
                  : []),
              ].map(({ id, label, desc, Icon }) => (
                <button key={id} onClick={() => set({ image_mode: id })} data-testid={`empire-imagemode-${id}`}
                  className={`p-3 rounded-sm border text-left ${form.image_mode === id ? "border-cyan-400/50 bg-cyan-400/10" : "border-white/10"}`}>
                  <Icon size={15} className="text-cyan-300" />
                  <p className="text-xs text-white mt-1">{label}</p>
                  <p className="text-[10px] text-slate-400">{desc}</p>
                </button>
              ))}
            </div>
            {form.image_mode === "own" && (
              <div className="mt-3">
                {form.image_url ? (
                  <span className="flex items-center gap-2 text-xs text-slate-300">
                    <img src={form.image_url} alt="" className="w-10 h-10 object-cover rounded-sm" />
                    {form.image_label || "Chosen image"}
                    <button onClick={() => set({ image_url: null, image_label: "" })} className="text-slate-500 hover:text-red-300"><X size={12} /></button>
                  </span>
                ) : (
                  <button onClick={() => setPickerOpen(true)} data-testid="empire-add-image"
                    className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-100">
                    <ImageIcon size={12} /> Pick or upload an image
                  </button>
                )}
              </div>
            )}
            {form.image_mode === "auto" && (
              <p className="text-[11px] text-slate-500 mt-2">A fresh, realistic photo is generated for each post (gpt-image-2).</p>
            )}
          </div>

          {/* Where */}
          <div>
            <p className="text-xs font-bold text-white mb-2">4. Where should it go?</p>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map(({ id, label, Icon }) => {
                const connected = connectedSet.has(id);
                const via = id === "facebook" || id === "instagram" ? "Meta" : "Ayrshare";
                return (
                  <button key={id} onClick={() => connected && toggle("channels", id)} disabled={!connected}
                    title={connected ? "" : `${label} not connected (${via})`}
                    data-testid={`empire-channel-${id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs border ${form.channels.includes(id) ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-400"} ${!connected ? "opacity-40 cursor-not-allowed" : ""}`}>
                    <Icon size={13} /> {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* When (detail) */}
          <div>
            <p className="text-xs font-bold text-white mb-2">5. When?</p>
            {form.type === "recurring" ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {DAYS.map((d) => (
                    <button key={d.id} onClick={() => toggle("days", d.id)} data-testid={`empire-day-${d.id}`}
                      className={`px-3 py-1.5 rounded-sm text-xs border ${form.days.includes(d.id) ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-400"}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500">Pick days, or leave empty for every day.</p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">at</span>
                  <input type="time" value={form.time} onChange={(e) => set({ time: e.target.value })}
                    data-testid="empire-time"
                    className="px-3 py-1.5 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
                  <span className="text-xs text-slate-500">UK time</span>
                </div>
              </div>
            ) : (
              <input type="datetime-local" value={form.run_at} onChange={(e) => set({ run_at: e.target.value })}
                data-testid="empire-runat"
                className="px-3 py-1.5 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm" />
            )}
          </div>

          {preview && (
            <div className="p-3 bg-[#050B14] border border-cyan-400/20 rounded-sm" data-testid="empire-preview">
              <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-1">Preview</p>
              {preview.image_url && (
                <img
                  src={preview.image_url}
                  alt="Featured"
                  data-testid="empire-preview-image"
                  className="w-full max-h-52 object-cover rounded-sm border border-white/10 mb-2"
                />
              )}
              <p className="text-sm text-slate-200 whitespace-pre-wrap">{preview.caption}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            <button onClick={() => { setShowForm(false); setEditingId(null); setPreview(null); }} className="px-4 py-2 text-slate-400 hover:text-white text-xs uppercase tracking-wider">Cancel</button>
            <button onClick={doPreview} disabled={previewing} data-testid="empire-preview-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-400/40 text-cyan-300 hover:border-cyan-400 rounded-sm text-xs uppercase tracking-wider">
              {previewing ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Preview
            </button>
            <button onClick={save} disabled={busy} data-testid="empire-save-btn"
              className="inline-flex items-center gap-2 px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-widest hover:bg-[#33F3FF] disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} {editingId ? "Update automation" : "Save automation"}
            </button>
          </div>
        </div>
      )}

      {/* Existing automations */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-300" /></div>
      ) : items.length === 0 && !showForm ? (
        <p className="text-slate-500 text-sm" data-testid="empire-empty">No automations yet — hit &ldquo;New automation&rdquo; to set your first one.</p>
      ) : (
        <div className="space-y-2" data-testid="empire-list">
          {items.map((a) => {
            const expanded = expandedId === a.automation_id;
            const last = (a.history || [])[a.history?.length - 1];
            const dayTxt = a.type === "oneoff"
              ? (a.run_at ? new Date(a.run_at).toLocaleString() : "unset")
              : ((a.days && a.days.length) ? a.days.join(", ") : "every day") + ` at ${a.time || "??:??"} UK`;
            return (
              <div key={a.automation_id} data-testid={`empire-item-${a.automation_id}`}
                className="bg-[#0A192F] border border-white/10 rounded-sm">
                <div className="flex items-center gap-3 p-4">
                  {a.type === "recurring" ? <Repeat size={16} className="text-cyan-300 shrink-0" /> : <Clock size={16} className="text-cyan-300 shrink-0" />}
                  <button
                    onClick={() => setExpandedId(expanded ? null : a.automation_id)}
                    data-testid={`empire-expand-${a.automation_id}`}
                    className="min-w-0 flex-1 text-left group"
                    title="Click to see details"
                  >
                    <p className="text-sm text-white capitalize group-hover:text-cyan-200 flex items-center gap-1.5">
                      <ChevronDown size={13} className={`text-slate-500 transition-transform ${expanded ? "rotate-180" : ""}`} />
                      {a.summary}
                    </p>
                    <p className="text-[11px] font-mono text-slate-500 pl-5">{a.enabled ? "active" : "paused"}{a.sent ? " · sent" : ""} · tap to view</p>
                  </button>
                  <button onClick={() => startEdit(a)} data-testid={`empire-edit-${a.automation_id}`}
                    className="text-slate-400 hover:text-cyan-300" title="Edit"><Pencil size={15} /></button>
                  <button onClick={() => toggleEnabled(a)} data-testid={`empire-toggle-${a.automation_id}`}
                    className="text-slate-400 hover:text-cyan-300" title={a.enabled ? "Pause" : "Resume"}>
                    {a.enabled ? <Pause size={15} /> : <Play size={15} />}
                  </button>
                  <button onClick={() => remove(a)} data-testid={`empire-delete-${a.automation_id}`}
                    className="text-slate-400 hover:text-red-300"><Trash2 size={15} /></button>
                </div>
                {expanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-2 text-xs" data-testid={`empire-details-${a.automation_id}`}>
                    <div className="grid sm:grid-cols-2 gap-y-1.5 gap-x-4 pt-3">
                      <p className="text-slate-400">Posts: <span className="text-slate-200">{a.source === "latest_blog" ? "My latest blog post" : "My own words"}</span></p>
                      <p className="text-slate-400">When: <span className="text-slate-200">{dayTxt}</span></p>
                      <p className="text-slate-400">Channels: <span className="text-slate-200">{(a.channels || []).join(", ") || "none"}</span></p>
                      <p className="text-slate-400">Status: <span className="text-slate-200">{a.enabled ? "Active" : "Paused"}</span></p>
                      <p className="text-slate-400">Image: <span className="text-slate-200">{{
                        auto: "Generated photo", own: "My own image", none: "No image", article: "Blog's photo",
                      }[a.image_mode] || (a.source === "latest_blog" ? "Blog's photo" : "Generated photo")}</span></p>
                    </div>
                    {a.source === "custom" && a.message && (
                      <p className="text-slate-400">Message: <span className="text-slate-200 whitespace-pre-wrap">{a.message}</span></p>
                    )}
                    {a.image_url && (
                      <img src={a.image_url} alt="" className="w-24 h-24 object-cover rounded-sm border border-white/10" />
                    )}
                    {last && (
                      <p className="text-slate-500 font-mono text-[11px]">
                        Last run: {last.status}{last.at ? ` · ${new Date(last.at).toLocaleString()}` : ""}
                        {last.detail?.reason ? ` — ${last.detail.reason}` : ""}
                      </p>
                    )}
                    <button onClick={() => startEdit(a)} data-testid={`empire-edit-inline-${a.automation_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-cyan-400/40 text-cyan-300 hover:border-cyan-400 rounded-sm text-[11px] uppercase tracking-wider mt-1">
                      <Pencil size={12} /> Edit this automation
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pickerOpen && (
        <ImagePicker onClose={() => setPickerOpen(false)}
          onPick={(url, label) => { set({ image_url: url, image_label: label }); setPickerOpen(false); }} />
      )}
    </div>
  );
};

export default EmpireBuilder;
